/**
 * The section 5 interaction FSM.
 *
 * One key. What it does is a function of three things: what the player holds,
 * what they face, and the state of that thing. Every row of the spec's table is
 * a branch below, and every branch that cannot proceed says why — a failure is
 * an `ACTION_FAILED` event that both clients receive, never silence.
 *
 * Two rules shape the whole module:
 *
 *   Contested actions are tick-level only. If two players act on the same cell
 *   on the same tick, neither succeeds and a `CONTESTED` goes to *both* clients
 *  . There is no window, no lockout and no retraction —
 *   inputs on different ticks are not contested at all: the second simply fails
 *   against the new state, and that failure is broadcast too. The Craft
 *   Together failure mode this fixes was never that the lock was too short, it
 *   was that resolution was invisible to the winner and private to the loser.
 *
 *   Held progress lives on the station and survives the key coming up. A press
 *   resumes, never restarts, and never picks the item back up while progress is
 *   partial — the ambiguity the single-key design has to avoid.
 */

import type {
  Carryable,
  Cell,
  Container,
  CookState,
  GameEvent,
  IngredientId,
  PlayerId,
  PlayerInput,
  Station,
} from '@overcooked/shared'
import { carryableContents, cellAhead, cellKey, isCookingEquipment } from '@overcooked/shared'
import type { Content } from './content'
import type { GameState } from './state'
import { stationAt } from './state'
import { stationOrder } from './stationOrder'
import { cloneContainer, cloneItem } from './cloneItem'
import { matchOrder, removeOrder } from './orders'
import {
  containerCapacity,
  containerFitsEquipment,
  cookableFor,
  durationTicks,
  extinguishTicks,
  phaseTicksFor,
  plateCapacity,
  plateReturnTicks,
  platesPerMap,
  transformFor,
  washTicks,
} from './rules'
import { scoreForServe, scoreForUnmatchedServe, ticketTicksRemaining } from './scoring'

/**
 * `edge` is the key going down and runs the full table. `hold` is a tick
 * between a press and its release: it may only *continue* chopping or washing,
 * and never reports a failure, because a held key is one decision rather than
 * fifty.
 */
type Press = 'edge' | 'hold'

/**
 * The subject of a wash that belongs to the sink's pile rather than to a plate
 * in somebody's hands. The pile is a count, and a plate minted off it has no
 * identity until it comes clean, so the progress belongs to "the top plate".
 */
export const SINK_PILE_SUBJECT = 'sink-pile'

/**
 * How long a pile wash that started from nothing may run before letting go
 * counts as a pause rather than as a tap.
 *
 * Dropping a plate on the sink is a tap, and a tap is a press edge followed by
 * a few synthesised hold ticks until the release arrives. Freezing those few
 * ticks would leave a `paused` sliver on the sink after every plate put down,
 * which reads as abandoned work where there is none. Ten ticks is 200 ms — a
 * quick tap, and a sliver of a four-second wash if it was a real hold.
 */
export const PILE_TAP_GRACE_TICKS = 10

interface Request {
  playerId: PlayerId
  seat: number
  press: Press
  cell: Cell
}

export function resolveInteractions(
  state: GameState,
  inputs: PlayerInput[],
  events: GameEvent[],
  content: Content,
): void {
  // A held key that has nothing to continue is not a claim on the cell. The
  // transport synthesises a hold on every tick the key is down, wherever the
  // player is facing, so without this a chef resting on the key in front of
  // the handover counter — or still finishing the tap that put a plate there —
  // turned every one of their partner's presses at that counter into a 💥.
  const requests = collect(state, inputs).filter(
    (request) => request.press === 'edge' || holdCanProgress(state, request, content),
  )

  // Group by the cell each player is facing. Movement has already resolved
  // this tick, so these are the cells they ended up facing.
  const byCell = new Map<string, Request[]>()
  for (const request of requests) {
    const key = cellKey(request.cell)
    const group = byCell.get(key)
    if (group) group.push(request)
    else byCell.set(key, [request])
  }

  /** Cells where a hold is genuinely progressing this tick. */
  const progressing = new Set<string>()

  for (const key of [...byCell.keys()].sort()) {
    const contenders = (byCell.get(key) as Request[]).sort((a, b) => a.seat - b.seat)

    if (contenders.length > 1) {
      // Nobody gets it, and the cell is left out of `progressing` below, so any
      // hold on it freezes. The 💥 is announced on a press edge only: two
      // players leaning on the key at one station is a single stand-off, not
      // fifty a second, and saying so fifty times a second would drown the log
      // and the canvas both.
      if (contenders.some((c) => c.press === 'edge')) {
        events.push({
          tick: state.tick,
          wallClock: 0,
          actor: 'system',
          type: 'CONTESTED',
          cell: { ...contenders[0].cell },
          playerIds: contenders.map((c) => c.playerId),
        })
      }
      continue
    }

    const request = contenders[0]
    if (applyInteraction(state, request, events, content)) progressing.add(key)
  }

  pauseAbandonedProgress(state, progressing, events)
}

/**
 * One interaction per player per tick, edge beating hold. A client that
 * key-repeats faster than the tick rate has the extras dropped rather than
 * queued, exactly as movement does.
 */
function collect(state: GameState, inputs: PlayerInput[]): Request[] {
  const press = new Map<PlayerId, Press>()

  for (const input of inputs) {
    const kind = input.action.kind
    if (kind !== 'interact' && kind !== 'interact_hold') continue
    const player = state.players[input.playerId]
    if (!player || !player.connected) continue
    if (kind === 'interact') press.set(input.playerId, 'edge')
    else if (!press.has(input.playerId)) press.set(input.playerId, 'hold')
  }

  return [...press.entries()]
    .map(([playerId, kind]) => {
      const player = state.players[playerId]
      return {
        playerId,
        seat: player.seat,
        press: kind,
        cell: cellAhead(player.cell, player.facing),
      }
    })
    .sort((a, b) => a.seat - b.seat)
}

/**
 * Whether a held key, alone on its cell, would start or continue one of the
 * three held jobs: a chop, a wash, or putting a fire out.
 *
 * This is the set of `hold` rows that do something in the table below (and in
 * `matrix.ts`, where `matrix.test.ts` checks the two agree). Every other held
 * key is silent there, and here it is not counted as a contender for the cell
 * — "same cell, same tick, nobody gets it" is about two people wanting the
 * station, and a key resting down wants nothing.
 */
export function holdCanProgress(state: GameState, request: Pick<Request, 'playerId' | 'cell'>, content: Content): boolean {
  const station = stationAt(state, request.cell)
  if (!station) return false
  const held = state.players[request.playerId]?.holding ?? null
  if (station.onFire) return held !== null && held.type === 'equipment' && held.id === 'extinguisher'
  if (station.type === 'board') {
    return (
      held === null &&
      station.item !== null &&
      station.item.type === 'ingredient' &&
      transformFor(content, 'board', station.item.id) !== undefined
    )
  }
  if (station.type === 'sink') {
    if (held === null) return station.dirtyPlates > 0
    return held.type === 'plate' && !held.plate.clean && held.plate.contents.length === 0
  }
  return false
}

/**
 * A station that was being held but saw no hold this tick freezes where it is.
 * That covers all four ways a hold ends — the key came up, the player walked
 * away, they turned to face something else, or another player contested the
 * cell — with one rule instead of four.
 */
function pauseAbandonedProgress(state: GameState, progressing: Set<string>, events: GameEvent[]): void {
  // Most ticks nobody is holding anything, and then there is nothing to pause.
  if (!anyHold(state.stations)) return
  for (const key of stationOrder(state)) {
    const station = state.stations[key]
    if (station.resumedAtTick === null) continue
    if (progressing.has(key)) continue

    const playerId = station.heldBy
    const accrued = Math.max(0, state.tick - station.resumedAtTick)
    const pileTap =
      station.type === 'sink' &&
      station.heldSubject === SINK_PILE_SUBJECT &&
      station.progressTicks === 0 &&
      accrued <= PILE_TAP_GRACE_TICKS
    // A plate dropped on the pile is a tap, not a wash abandoned part-way.
    if (!pileTap) station.progressTicks += accrued
    station.resumedAtTick = null
    station.heldBy = null

    if (playerId) emitProgress(state, station, playerId, events)
  }
}

function anyHold(stations: Record<string, Station>): boolean {
  for (const key in stations) if (stations[key].resumedAtTick !== null) return true
  return false
}

/**
 * Which of the three held jobs a station is running.
 *
 * A burning station can only ever be being put out — every other interaction
 * with it is refused — so `onFire` alone identifies the job without a fourth
 * field on `Station` that every clone and every keyframe would have to carry.
 */
function progressEventType(station: Station): 'EXTINGUISH_PROGRESS' | 'WASH_PROGRESS' | 'CHOP_PROGRESS' {
  if (station.onFire) return 'EXTINGUISH_PROGRESS'
  return station.type === 'sink' ? 'WASH_PROGRESS' : 'CHOP_PROGRESS'
}

function emitProgress(state: GameState, station: Station, playerId: PlayerId, events: GameEvent[]): void {
  events.push({
    tick: state.tick,
    wallClock: 0,
    actor: playerId,
    type: progressEventType(station),
    playerId,
    cell: { ...station.cell },
    progressTicks: station.progressTicks,
    targetTicks: station.progressTargetTicks,
    resumedAtTick: station.resumedAtTick,
  })
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

/** Returns true when this tick counts as held progress on the faced station. */
function applyInteraction(
  state: GameState,
  request: Request,
  events: GameEvent[],
  content: Content,
): boolean {
  const { playerId, press, cell } = request
  const player = state.players[playerId]
  const station = stationAt(state, cell)

  const fail = (reason: string): boolean => {
    // A held key repeats the same refusal fifty times a second; say it once.
    if (press === 'edge') {
      events.push({ tick: state.tick, wallClock: 0, actor: playerId, type: 'ACTION_FAILED', playerId, reason })
    }
    return false
  }

  const pickUp = (from: Station): boolean => {
    const item = from.item
    if (!item) return fail('nothing_there')
    player.holding = cloneItem(item)
    from.item = null
    from.progressTicks = 0
    from.progressTargetTicks = 0
    from.resumedAtTick = null
    from.heldBy = null
    from.heldSubject = null
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: playerId,
      type: 'PICKED_UP',
      playerId,
      cell: { ...from.cell },
      item: cloneItem(item),
    })
    return false
  }

  /**
   * Take the container off cooking equipment. The other slot, same event.
   *
   * The phase stamps are dropped on the way out, because the clock belongs to
   * the heat and not to the pan: a burnt pan carried to the bin must not catch
   * fire in somebody's hands, and must not resume a deadline that expired
   * during the walk when it is finally set down again.
   */
  const pickUpContainer = (from: Station): boolean => {
    const container = from.container
    if (!container) return fail('nothing_there')
    const lifted: Container = {
      ...cloneContainer(container),
      phaseStartedAtTick: null,
      phaseEndsAtTick: null,
    }
    const item: Carryable = { type: 'container', container: lifted }
    player.holding = cloneItem(item)
    from.container = null
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: playerId,
      type: 'PICKED_UP',
      playerId,
      cell: { ...from.cell },
      item,
    })
    return false
  }

  const putDown = (onto: Station): boolean => {
    const item = player.holding
    if (!item) return fail('nothing_to_put_down')
    // One `PUT_DOWN`, three slots: cooking equipment keeps its container where
    // the cook clock can see it, the clean stack takes a plate back as height
    // rather than as an object, and everything else keeps whatever it holds in
    // `item`. The fold applies exactly the same rule.
    if (onto.type === 'plate_stack') onto.cleanPlates += 1
    else if (onto.type === 'sink') onto.dirtyPlates += 1
    else if (item.type === 'container' && isCookingEquipment(onto.type)) onto.container = cloneContainer(item.container)
    else onto.item = cloneItem(item)
    player.holding = null
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: playerId,
      type: 'PUT_DOWN',
      playerId,
      cell: { ...onto.cell },
      item: cloneItem(item),
    })
    return false
  }

  /**
   * Begin or continue a hold. Resumes from `progressTicks`, never restarts.
   *
   * `subject` is what the progress belongs to when the station cannot hold it
   * itself — the plate in the washer's hands. Progress that was earned on a
   * different subject is not this job's progress, so it is dropped rather than
   * inherited: that is the difference between "put the plate down for a second"
   * and "wash a second plate for free".
   */
  const beginOrContinueHold = (on: Station, targetTicks: number, subject: string | null = null): boolean => {
    if (on.resumedAtTick !== null && on.heldBy === playerId && on.heldSubject === subject) return true
    if (on.heldSubject !== subject) on.progressTicks = 0
    on.heldSubject = subject
    on.progressTargetTicks = targetTicks
    on.resumedAtTick = state.tick
    on.heldBy = playerId
    emitProgress(state, on, playerId, events)
    return true
  }

  if (!station) return fail('nothing_there')

  const held = player.holding

  // Time constraint #4, and the whole of "carried equipment changes the meaning
  // of [interaction]". A burning station is inert to every other press there
  // is; with the extinguisher in hand the same key puts it out. One key, a
  // state-dependent meaning.
  if (station.onFire) {
    if (!held || held.type !== 'equipment' || held.id !== 'extinguisher') {
      return fail('station_on_fire')
    }
    return beginOrContinueHold(station, extinguishTicks(content))
  }

  switch (station.type) {
    case 'wall':
    case 'floor':
      return fail('nothing_there')

    // nothing | crate -> take one ingredient. Crates never empty.
    case 'crate': {
      // Edges only, like every other single-press station. `loop.ts` synthesises
      // an `interact_hold` on every tick the key is down, so without this a
      // player who held to chop and then walked away without letting go is
      // handed an onion the moment they face a crate with empty hands.
      if (press === 'hold') return false
      if (held) return fail('hands_full')
      if (!station.dispenses) return fail('crate_empty')
      player.holding = { type: 'ingredient', id: station.dispenses }
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: playerId,
        type: 'PICKED_UP',
        playerId,
        cell: { ...station.cell },
        item: { type: 'ingredient', id: station.dispenses },
      })
      return false
    }

    // nothing | plate stack -> take one clean plate.
    // clean empty plate | plate stack -> put it back, so a plate in hand always
    // has a home even when every counter in the kitchen is occupied.
    case 'plate_stack': {
      if (press === 'hold') return false

      if (held) {
        if (held.type !== 'plate') return fail('stack_takes_plates')
        if (!held.plate.clean) return fail('plate_is_dirty')
        if (held.plate.contents.length > 0) return fail('plate_is_loaded')
        if (station.cleanPlates >= platesPerMap(content)) return fail('stack_is_full')
        return putDown(station)
      }

      if (station.cleanPlates <= 0) return fail('no_clean_plates')
      // Ids are derived from the stack cell and its remaining count, so they
      // are unique across stacks and identical on every replay.
      const plateId = `plate-${station.cell.x}-${station.cell.y}-${station.cleanPlates}`
      station.cleanPlates -= 1
      const item = { type: 'plate' as const, plate: { id: plateId, clean: true, contents: [] } }
      player.holding = cloneItem(item)
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: playerId,
        type: 'PICKED_UP',
        playerId,
        cell: { ...station.cell },
        item,
      })
      return false
    }

    // nothing | counter with item -> pick up. anything | counter, empty -> put down.
    case 'counter': {
      if (press === 'hold') return false
      if (!held) return station.item ? pickUp(station) : fail('counter_empty')
      return station.item ? fail('counter_occupied') : putDown(station)
    }

    case 'board': {
      if (!held) {
        if (!station.item) return press === 'hold' ? false : fail('board_empty')
        if (station.item.type !== 'ingredient') return press === 'hold' ? false : pickUp(station)

        // The board is the FSM-of-inclusion exception: it holds one ingredient
        // while transforming it. While a chop is possible the
        // key chops; the item only comes back off once there is nothing left
        // to do to it, which is what stops a press being ambiguous.
        const transform = transformFor(content, 'board', station.item.id)
        if (transform) return beginOrContinueHold(station, durationTicks(content, transform.durationKey))
        return press === 'hold' ? false : pickUp(station)
      }

      if (press === 'hold') return false
      // "Containers CANNOT go into ingredients" — you carry the ingredient and
      // interact with the container, never the reverse. The board only ever
      // takes a bare ingredient.
      if (held.type !== 'ingredient') return fail('board_takes_ingredients')
      return station.item ? fail('board_occupied') : putDown(station)
    }

    // Cooking equipment. It holds a container; the container holds the food.
    // An ingredient offered to bare equipment is refused, which is the FSM of
    // inclusion's "ingredients CANNOT go into static equipment" written out.
    case 'stove':
    case 'oven': {
      if (press === 'hold') return false
      const container = station.container

      if (!container) {
        if (!held) return fail('no_container')
        if (held.type !== 'container') return fail('no_container')
        // Only an empty pan goes on the heat. Parking a burnt one on the next
        // stove would be a way of dodging the walk to the bin, and it would
        // hand the kitchen back a pan whose contents nothing can now remove.
        if (held.container.contents.length > 0) return fail('pan_burnt')
        if (!containerFitsEquipment(content, held.container.kind, station.type)) {
          return fail('container_does_not_fit')
        }
        return putDown(station)
      }

      if (!held) {
        // Burnt contents cannot be plated, and they no longer vanish where they
        // stand: the pan comes off the heat and somebody walks it to the bin.
        // That is what the original game does, and it is what makes the fire
        // window a window to *do* something in rather than to press once.
        //
        // An empty container comes off the heat too; a cooking or done one does
        // not, so a press is never ambiguous between "take the pan" and "do the
        // thing the pan is for".
        if (container.cookState === 'empty' || container.cookState === 'burnt') {
          return pickUpContainer(station)
        }
        if (container.cookState === 'cooking') return fail('still_cooking')
        return fail('needs_a_plate')
      }

      if (held.type === 'equipment') return fail('put_the_equipment_down')

      if (held.type === 'container') return fail('equipment_occupied')

      if (held.type === 'ingredient') {
        if (container.cookState === 'burnt') return fail('pan_burnt')
        if (container.cookState === 'done') return fail('needs_a_plate')
        if (container.contents.length >= containerCapacity(content, container.kind)) {
          return fail('pan_full')
        }
        const cookable = cookableFor(content, station.type, container.kind, held.id)
        if (!cookable) {
          // Two different mistakes, and the player needs telling which. Raw
          // meat at the stove wants the board; a chopped carrot at the oven is
          // ready, it is just at the wrong heat — and "chop it first" said to
          // somebody holding a chopped carrot reads as the game being broken.
          if (content.recipes.cookable.some((c) => c.from === held.id)) return fail('cooks_elsewhere')
          return fail('not_cookable')
        }

        const contents = [...container.contents, held.id]
        player.holding = null
        events.push({
          tick: state.tick,
          wallClock: 0,
          actor: playerId,
          type: 'PAN_FILLED',
          playerId,
          cell: { ...station.cell },
          ingredient: held.id,
        })
        // Cooking (re)starts from now: a pan is done when its slowest
        // ingredient is, and dropping a fourth thing in visibly resets the bar.
        const toDone = phaseTicksFor(content, station.type, container, 'toDoneKey', contents)
        const endsAt = state.tick + (toDone ?? durationTicks(content, cookable.toDoneKey))
        setContainer(state, station, 'cooking', contents, state.tick, endsAt, events)
        return false
      }

      // plate | container, contents done -> contents transfer to the plate.
      //
      // The plate is where a dish is assembled now that a pan cooks one thing
      // at a time, which is why it has a limit of its own: an unbounded plate
      // is a pile a player can only undo at the bin, and `plateCapacity` is
      // shipped as the longest dish's length so nothing servable is ever
      // refused.
      if (container.cookState === 'empty') return fail('pan_empty')
      if (container.cookState === 'cooking') return fail('still_cooking')
      if (container.cookState === 'burnt') return fail('contents_burnt')
      if (!held.plate.clean) return fail('plate_is_dirty')
      if (held.plate.contents.length + container.contents.length > plateCapacity(content)) {
        return fail('plate_is_full')
      }

      held.plate.contents = [...held.plate.contents, ...container.contents]
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: playerId,
        type: 'PLATED',
        playerId,
        cell: { ...station.cell },
        plateId: held.plate.id,
        contents: [...held.plate.contents],
      })
      setContainer(state, station, 'empty', [], null, null, events)
      return false
    }

    // The sink keeps a pile of dirty plates, the way the plate return does, so
    // washing up no longer clogs the counters (playtest: "put them down
    // together, take them one by one").
    //
    //   nothing     | sink with a pile -> wash the top plate while held; it
    //                                     comes off the pile clean, into the hand
    //   dirty plate | sink, press      -> onto the pile, and the same press
    //                                     starts washing the top plate — so a
    //                                     tap stacks and a press-and-hold still
    //                                     ends with a clean plate in hand
    //   dirty plate | sink, held key   -> washed in hand (walked up with the
    //                                     key already down; today's behaviour)
    case 'sink': {
      if (!held) {
        if (station.dirtyPlates > 0) return beginOrContinueHold(station, washTicks(content), SINK_PILE_SUBJECT)
        return press === 'hold' ? false : fail('nothing_to_wash')
      }
      if (held.type !== 'plate') return press === 'hold' ? false : fail('sink_takes_plates')
      if (held.plate.clean) return press === 'hold' ? false : fail('plate_already_clean')
      if (held.plate.contents.length > 0) return press === 'hold' ? false : fail('plate_is_loaded')
      // The plate id is the subject: progress towards washing *this* plate,
      // not towards washing whatever is presented next.
      if (press === 'hold') return beginOrContinueHold(station, washTicks(content), held.plate.id)
      // A full pile cannot happen with a conserved plate count; if it ever
      // does, washing in hand is still a legal press rather than a refusal.
      if (station.dirtyPlates >= platesPerMap(content)) {
        return beginOrContinueHold(station, washTicks(content), held.plate.id)
      }
      putDown(station)
      return beginOrContinueHold(station, washTicks(content), SINK_PILE_SUBJECT)
    }

    // food | bin -> the food is thrown away. Everything else is refused, and
    // told where it actually goes.
    //
    // The bin deals with food and nothing else, which is what the original game
    // does. It used to be the kitchen's teleporter: press it with the
    // extinguisher and the extinguisher appeared on a rack across the room,
    // press it with an empty plate and the plate appeared on the stack. Both
    // are gone. A thing with a home of its own is walked to that home.
    //
    // The recoverability invariant survives the change, because it never needed
    // the bin to be a universal exit — it needs every carryable to have *some*
    // station that will take it, and `validateMap` already insists a kitchen
    // has all of them: the stack and the sink for plates, a rack for the
    // extinguisher, a stove or an oven or any free counter for a pan, and the
    // bin itself for food. `recovery.test.ts` walks each of those exits.
    case 'bin': {
      if (press === 'hold') return false
      if (!held) return fail('nothing_to_bin')
      if (held.type === 'container') {
        // This is where burnt food ends up. The bin tips the pan out and the
        // player keeps it, exactly as it tips a loaded plate out — a pan is
        // the kitchen's equipment, not rubbish. An already-empty pan has
        // nothing here to do, so it is refused rather than silently accepted.
        if (held.container.contents.length === 0) return fail('container_is_empty')
        events.push({
          tick: state.tick,
          wallClock: 0,
          actor: playerId,
          type: 'PAN_EMPTIED',
          playerId,
          cell: { ...station.cell },
          wasBurnt: held.container.cookState === 'burnt',
        })
        setHeldContainer(state, player, station.cell, 'empty', [], null, null, events)
      } else if (held.type === 'equipment') {
        // The extinguisher has a rack, and the walk back to it is the point.
        // A kitchen that has quietly lost track of where its extinguisher is
        // is a kitchen that loses its next stove.
        return fail('rack_the_extinguisher')
      } else if (held.type === 'plate' && held.plate.contents.length > 0) {
        // Tipping the food out and giving the plate up are different decisions.
        // This press is the first one; there is no second one — the bare plate
        // goes to the stack or the sink, which is the refusal below.
        held.plate.contents = []
      } else if (held.type === 'plate') {
        return fail('plates_go_home')
      } else player.holding = null

      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: playerId,
        type: 'TRASHED',
        playerId,
        cell: { ...station.cell },
      })
      return false
    }

    // plate | serving hatch -> serve. Plates only.
    case 'hatch': {
      if (press === 'hold') return false
      if (!held) return fail('nothing_to_serve')
      if (held.type !== 'plate') return fail('plates_only')
      if (!held.plate.clean) return fail('plate_is_dirty')

      const contents = carryableContents(held)
      const plateId = held.plate.id
      player.holding = null

      // The queue decides what this was worth. A plate nobody ordered still
      // serves and still comes back dirty — it simply scores nothing (spec
      // section 12), so `matchedOrderId: null` is an outcome, not an error.
      const matched = matchOrder(state, contents, content)
      const remaining = matched ? ticketTicksRemaining(matched, state.tick) : 0
      const scoreDelta = matched
        ? scoreForServe(content, matched, state.tick)
        : scoreForUnmatchedServe(content)
      if (matched) removeOrder(state, matched.id)
      state.score += scoreDelta

      const returnsAtTick = state.tick + plateReturnTicks(content)
      state.pendingPlates = [...state.pendingPlates, { plateId, dueAtTick: returnsAtTick }]
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: playerId,
        type: 'SERVED',
        playerId,
        plateId,
        contents,
        matchedOrderId: matched?.id ?? null,
        // Carried on the event so scoring can be recomputed offline from the
        // log without replaying the round.
        ticketTicksRemaining: remaining,
        scoreDelta,
        returnsAtTick,
      })
      return false
    }

    // nothing | rack with the extinguisher -> pick it up. Holding it -> put it
    // back. Nothing else may be racked, so it cannot be lost behind a plate.
    case 'extinguisher_rack': {
      if (press === 'hold') return false
      if (!held) return station.item ? pickUp(station) : fail('rack_empty')
      if (held.type !== 'equipment') return fail('rack_takes_equipment')
      return station.item ? fail('rack_occupied') : putDown(station)
    }

    // nothing | plate return with plates -> take one dirty plate off the pile.
    //
    // The pile is a count, not a slot: three served plates all coming back at
    // once used to leave two of them stuck in the return queue behind the one
    // the station could hold.
    case 'plate_return': {
      if (press === 'hold') return false
      if (held) return fail('plate_return_takes_nothing')
      if (station.dirtyPlates <= 0) return fail('plate_return_empty')

      // Derived from the cell and the remaining count, exactly as the clean
      // stack derives its ids: a returned plate's identity is spent, and
      // nothing downstream of the return reads it.
      const plateId = `plate-${station.cell.x}-${station.cell.y}-${station.dirtyPlates}`
      station.dirtyPlates -= 1
      const item = { type: 'plate' as const, plate: { id: plateId, clean: false, contents: [] } }
      player.holding = cloneItem(item)
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor: playerId,
        type: 'PICKED_UP',
        playerId,
        cell: { ...station.cell },
        item,
      })
      return false
    }
  }
}

// ---------------------------------------------------------------------------
// Where the kitchen's fixed possessions live
//
// There is exactly one extinguisher per rack and exactly `platesPerMap` plates,
// and both are needed to finish a round. Neither is ever destroyed, and — since
// the bin stopped sending them home for free — neither ever moves without
// somebody carrying it: an extinguisher goes back to a rack and a plate to the
// stack or the sink, on foot. The helpers that used to teleport them
// (`freeRackFor`, `rackEquipment`, `freePlateStation`, `stowPlate`) are gone
// with the branches that called them. `EQUIPMENT_RACKED` and `PLATE_STOWED`
// survive in the event union so that logs recorded before this change still
// fold; nothing emits them any more.
// ---------------------------------------------------------------------------

/**
 * The single writer of container state. Every change emits
 * `COOK_STATE_CHANGED` carrying the container's id, the resulting contents and
 * both stamps, so a replay reconstructs it without ever opening `recipes.json`
 * — and finds it wherever it has got to, since a container is no longer welded
 * to the cell the event names.
 */
export function setContainer(
  state: GameState,
  station: Station,
  cookState: CookState,
  contents: IngredientId[],
  startedAtTick: number | null,
  endsAtTick: number | null,
  events: GameEvent[],
): void {
  const container: Container | null = station.container
  if (!container) return
  container.cookState = cookState
  container.contents = [...contents]
  container.phaseStartedAtTick = startedAtTick
  container.phaseEndsAtTick = endsAtTick
  events.push({
    tick: state.tick,
    wallClock: 0,
    actor: 'system',
    type: 'COOK_STATE_CHANGED',
    cell: { ...station.cell },
    containerId: container.id,
    state: cookState,
    contents: [...contents],
    startedAtTick,
    endsAtTick,
  })
}

/**
 * The same writer, for a container that is in somebody's hands.
 *
 * `setContainer` takes the station a pan is standing on, and the bin is not
 * that station — the pan being tipped out there is carried. Rather than let the
 * bin mutate a container directly, it goes through this sibling, so a change to
 * a pan still means exactly one `COOK_STATE_CHANGED` in the log whichever of
 * the two slots the pan is in. The event addresses the container by id, and
 * `findContainer` in the fold already looks in players' hands, so the replay
 * needs no new lookup; `cell` is where the change *happened*, which is the bin.
 */
export function setHeldContainer(
  state: GameState,
  player: { id: PlayerId; holding: Carryable | null },
  cell: Cell,
  cookState: CookState,
  contents: IngredientId[],
  startedAtTick: number | null,
  endsAtTick: number | null,
  events: GameEvent[],
): void {
  const held = player.holding
  if (!held || held.type !== 'container') return
  const container = held.container
  container.cookState = cookState
  container.contents = [...contents]
  container.phaseStartedAtTick = startedAtTick
  container.phaseEndsAtTick = endsAtTick
  events.push({
    tick: state.tick,
    wallClock: 0,
    actor: 'system',
    type: 'COOK_STATE_CHANGED',
    cell: { ...cell },
    containerId: container.id,
    state: cookState,
    contents: [...contents],
    startedAtTick,
    endsAtTick,
  })
}
